/* Global API auth for fetch (God 2026-09-27: "bots kay undar kay symbol and tf hat gai hay").

   Since the 2026-09-26 multi-tenant change (TP1) the engine answers 401 to an
   anonymous caller on /deployments, /api/trades/ledger, /api/bots/*, /forensics/*,
   /scanner/* and more. ~40 dashboard fetches never sent the login token, so the
   Fleet Registry got 401 for /deployments and drew every bot with no symbols,
   no timeframes and "syncing..." forever.

   One place fixes all of them: every fetch to OUR API host that carries no auth
   header of its own gets `Authorization: Bearer <jc_auth_token>`.
   - Only https://api.jaiswalcapital.com (or VITE_API_URL's origin): the token
     never goes to any third-party host.
   - Not /bridge/* (the Mamta bridge allows only content-type + x-mamta-token in
     CORS, so an extra header would fail its preflight) and not /socket.io/*.
   - A request that already sends Authorization / X-Session-Token / x-mamta-token
     is left exactly as it is. No token stored -> nothing is added. */

const DEFAULT_API_ORIGIN = 'https://api.jaiswalcapital.com';
const SKIP_PATHS = [/^\/bridge\//, /^\/socket\.io\//];
const OWN_AUTH = ['authorization', 'x-session-token', 'x-mamta-token'];

type FetchArgs = [RequestInfo | URL, RequestInit?];

export function apiOrigins(): string[] {
  const out = [DEFAULT_API_ORIGIN];
  try {
    const env = (import.meta as unknown as { env?: Record<string, string> }).env || {};
    for (const k of ['VITE_API_URL', 'VITE_API_BASE_URL']) {
      if (env[k]) out.push(new URL(env[k]).origin);
    }
  } catch { /* a bad env URL never breaks fetch */ }
  return out;
}

/** True when this URL is our API and a path that wants the login token. */
export function wantsApiAuth(url: string, origins: string[] = apiOrigins()): boolean {
  let u: URL;
  try { u = new URL(url); } catch { return false; }          // relative / garbage: not ours to touch
  if (!origins.includes(u.origin)) return false;
  return !SKIP_PATHS.some(re => re.test(u.pathname));
}

function hasOwnAuth(h: Headers): boolean {
  return OWN_AUTH.some(k => !!h.get(k));
}

/** The fetch arguments with the bearer added when the request needs it; otherwise the same arguments. */
export function withApiAuth(args: FetchArgs, token: string, origins: string[] = apiOrigins()): FetchArgs {
  if (!token) return args;
  const [input, init] = args;
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
  if (!wantsApiAuth(url, origins)) return args;
  if (typeof Request !== 'undefined' && input instanceof Request && !init) {
    const h = new Headers(input.headers);
    if (hasOwnAuth(h)) return args;
    h.set('Authorization', 'Bearer ' + token);
    return [new Request(input, { headers: h }), undefined];
  }
  const h = new Headers((init && init.headers) || (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined));
  if (hasOwnAuth(h)) return args;
  h.set('Authorization', 'Bearer ' + token);
  return [input, { ...(init || {}), headers: h }];
}

function storedToken(): string {
  try { return localStorage.getItem('jc_auth_token') || ''; } catch { return ''; }
}

/** Wrap window.fetch once. Safe to call again (no double wrap). */
export function installApiAuthFetch(w: Window & { __jcApiAuthFetch?: boolean } = window): void {
  if (w.__jcApiAuthFetch || typeof w.fetch !== 'function') return;
  const orig = w.fetch.bind(w);
  const origins = apiOrigins();
  w.fetch = ((...args: FetchArgs) => {
    let a = args;
    try { a = withApiAuth(args, storedToken(), origins); } catch { a = args; }   // never break a fetch
    return orig(...a);
  }) as typeof w.fetch;
  w.__jcApiAuthFetch = true;
}
